import React, { useContext, useEffect, useRef, useState } from "react";
import {
	AnimatePresence,
	motion,
	useScroll,
	useTransform,
} from "framer-motion";
import { FaChevronDown, FaChevronRight, FaChevronUp } from "react-icons/fa";
import {
	IoIosArrowDown,
	IoIosArrowUp,
	IoIosClose,
	IoIosMenu,
} from "react-icons/io";
import { useNavigate } from "react-router-dom";
import categories from "../../utils/categories.json";
import ProfileIcon from "../common/Header/ProfileIcon";
import { AuthContext } from "../../context/AuthContext";

const AnimateNav = () => {
	const { scrollY } = useScroll();
	const [isOpen, setIsOpen] = useState(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isCoursesOpen, setIsCoursesOpen] = useState(false);
	const navigate = useNavigate();
	const dropdownRef = useRef(null);
	const toggleRef = useRef(null);
	const { user } = useContext(AuthContext);
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target) &&
				toggleRef.current &&
				!toggleRef.current.contains(event.target)
			) {
				setIsOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "auto";
		}

		return () => {
			document.body.style.overflow = "auto";
		};
	}, [isOpen]);

	const toggleDropdown = () => {
		setIsOpen((prev) => !prev);
	};

	const toggleMenuDropdown = () => {
		setIsMenuOpen((prev) => !prev);
	};

	const pageNavigate = (id) => {
		navigate(`/${id}`);
	};

	const scale = useTransform(scrollY, [0, 300], [1, 0.25]);
	const y = useTransform(scrollY, [0, 300], [0, 120]);
	const x = useTransform(scrollY, [0, 100], [0, 0]);

	const navY = useTransform(scrollY, [0, 150], [-100, 0]);

	return (
		<div className=" w-full">
			<motion.div
				style={{ y: navY }}
				className="sticky top-0 justify-between items-center pt-6 px-10 text-sm font-medium h-fit hidden lg:flex"
			>
				{/* left side */}
				<div className="w-2/5 flex justify-start items-center gap-8 ml-6 select-none">
					<button
						ref={toggleRef}
						className="flex items-center gap-2"
						onClick={toggleDropdown}
					>
						<span>Courses </span>
						{isOpen ? (
							<FaChevronUp className="text-sm font-extralight" />
						) : (
							<FaChevronDown className="text-sm font-extralight" />
						)}
					</button>
					<button onClick={() => pageNavigate("about")}>About</button>
					<button onClick={() => pageNavigate("pages")}>Pages</button>
					<button onClick={() => pageNavigate("contact")}>
						Contact
					</button>
				</div>

				{/* right side */}
				<div className="w-2/5 flex justify-end items-center gap-8 mr-6">
					<button onClick={() => pageNavigate("help")}>Help</button>
					<button onClick={() => pageNavigate("blog")}>Blog</button>
					<button>EN</button>

					{user ? (
						<ProfileIcon />
					) : (
						<>
							<button onClick={() => pageNavigate("auth")}>
								Login
							</button>
							<button
								onClick={() => pageNavigate("auth")}
								className="bg-black text-white text-xs px-6 py-3 rounded-full"
							>
								Signup
							</button>
						</>
					)}
				</div>
			</motion.div>
			<motion.div
				style={{ y: navY }}
				className="sticky top-0 justify-end items-center pt-8 px-10 text-sm font-medium h-fit lg:hidden flex"
			>
				<div className="flex justify-end items-center text-2xl">
					<button onClick={toggleMenuDropdown}>
						{" "}
						<IoIosMenu />
					</button>
				</div>
			</motion.div>
			<AnimatePresence>
				{isOpen && (
					<motion.div
						ref={dropdownRef}
						className="fixed top-20 left-10 w-7/12 h-[70vh] bg-white z-50 shadow-2xl rounded-xl flex justify-center p-4"
						initial={{ opacity: 0, y: -50 }}
						animate={{
							opacity: 1,
							y: 0,
						}}
						exit={{
							opacity: 0,
							y: -50,
							height: 0,
						}}
						transition={{ duration: 0.3 }}
					>
						<div className="w-2/5 flex flex-col justify-between h-full">
							<span className="text-sm font-semibold">
								Categories
							</span>
							<div className="flex flex-col space-y-4 py-5 px-2 overflow-y-auto flex-grow no-scrollbar">
								{categories.map((category) => (
									<div
										key={category.id}
										className="flex space-x-5 px-4 py-2 items-center bg-gray-100 hover:bg-gray-200 text-sm transition-all duration-200 ease-in-out rounded-lg cursor-pointer"
									>
										<img
											src={category.image}
											alt={category.category}
											className="w-12 h-12 rounded-lg object-cover"
										/>

										<div className="flex-1">
											<h1 className="text-black font-medium capitalize">
												{category.category}
											</h1>
										</div>

										<div>
											<FaChevronRight className="text-gray-500 group-hover:text-black transition-colors duration-200" />
										</div>
									</div>
								))}
							</div>

							<motion.div
								initial={{ opacity: 0 }}
								animate={{
									opacity: 1,
								}}
								exit={{
									opacity: 0,
									height: 0,
								}}
								transition={{ duration: 0.3 }}
								className="p-4 mr-4 bg-black rounded-lg hover:shadow-lg transition-shadow duration-200 flex-shrink-0"
							>
								<h2 className="text-white font-medium text-base">
									New Features
								</h2>
								<p className="text-gray-300 text-xs mt-2">
									Stay tuned for our latest updates and
									features, coming soon!
								</p>
							</motion.div>
						</div>

						{/* Right Side Content */}
						<div className="w-3/5 border-l border-gray-300 px-4 flex flex-col justify-between h-full">
							<div className="text-gray-600 flex-grow">
								<p className="text-lg font-semibold">
									Right Side Content
								</p>
								<p className="text-sm">Courses go here.</p>
							</div>
						</div>
					</motion.div>
				)}
			</AnimatePresence>

			<motion.div
				style={{ scale, y, x }}
				className="sticky top-1 flex justify-center items-center md:pt-60 pt-64"
			>
				<motion.div
					initial={{ scale: 0.2 }}
					animate={{ scale: 1 }}
					transition={{ duration: 0.6 }}
					className="lg:text-8xl md:text-6xl text-4xl font-semibold flex justify-center items-center select-none"
				>
					<motion.div
						initial={{ scale: 0.2 }}
						animate={{ scale: 1 }}
						transition={{
							type: "tween",
							duration: 1,
							ease: [0.34, 1.4, 0.64, 1],
						}}
						className="flex justify-center items-center"
					>
						<span>E-learning.</span>
						<span className="text-gray-500">io</span>
					</motion.div>
				</motion.div>
			</motion.div>

			<AnimatePresence>
				{isMenuOpen && (
					<motion.div
						ref={dropdownRef}
						key="menuDropdown"
						initial={{ opacity: 0, height: 0 }}
						animate={{ opacity: 1, height: "100vh" }}
						exit={{ opacity: 0, height: 0 }}
						transition={{ duration: 0.3 }}
						className="fixed top-0 left-0 right-0 mx-auto w-full bg-white flex flex-col items-center space-y-4 p-6 z-50 overflow-y-auto no-scrollbar"
					>
						<div
							className="absolute top-6 right-6 text-3xl cursor-pointer hover:text-gray-600 transition"
							onClick={toggleMenuDropdown}
						>
							<IoIosClose />
						</div>

						<div className="py-16 flex flex-col space-y-6 w-full text-sm">
							<div
								className="px-5 py-3 bg-black text-white font-medium border border-gray-500 rounded-xl flex justify-between items-center cursor-pointer"
								onClick={() => setIsCoursesOpen(!isCoursesOpen)}
							>
								<button>Courses</button>
								<div className="text-lg">
									{isCoursesOpen ? (
										<IoIosArrowUp />
									) : (
										<IoIosArrowDown />
									)}
								</div>
							</div>
							<AnimatePresence>
								{isCoursesOpen && (
									<motion.div
										ref={dropdownRef}
										key="courseDropdown"
										initial={{ opacity: 0, height: 0 }}
										animate={{ opacity: 1, height: "40vh" }}
										exit={{ opacity: 0, height: 0 }}
										transition={{ duration: 0.3 }}
										className="w-full flex justify-center px-2"
									>
										<div className="w-full flex flex-col">
											<div className="flex flex-col justify-between h-full">
												<div className="flex flex-col space-y-4 overflow-y-auto flex-grow no-scrollbar">
													{categories.map(
														(category) => (
															<div
																key={
																	category.id
																}
																className="flex space-x-5 px-4 py-2 items-center bg-gray-100 hover:bg-gray-200 text-sm transition-all duration-200 ease-in-out rounded-lg cursor-pointer"
															>
																<img
																	src={
																		category.image
																	}
																	alt={
																		category.category
																	}
																	className="w-12 h-12 rounded-lg object-cover"
																/>
																<div className="flex-1">
																	<h1 className="text-black font-medium capitalize">
																		{
																			category.category
																		}
																	</h1>
																</div>
																<div>
																	<FaChevronRight className="text-gray-500 group-hover:text-black transition-colors duration-200" />
																</div>
															</div>
														)
													)}
												</div>
											</div>
										</div>
									</motion.div>
								)}
							</AnimatePresence>

							<div
								className="px-5 py-3 font-medium border border-gray-500 rounded-xl"
								onClick={() => pageNavigate("about")}
							>
								<button>About</button>
							</div>

							<div
								className="px-5 py-3 font-medium border border-gray-500 rounded-xl"
								onClick={() => pageNavigate("pages")}
							>
								<button>Pages</button>
							</div>

							<div
								className="px-5 py-3 font-medium border border-gray-500 rounded-xl"
								onClick={() => pageNavigate("contact")}
							>
								<button>Contact</button>
							</div>

							<div
								className="px-5 py-3 font-medium border border-gray-500 rounded-xl"
								onClick={() => pageNavigate("help")}
							>
								<button>Help</button>
							</div>

							<div
								className="px-5 py-3 font-medium border border-gray-500 rounded-xl"
								onClick={() => pageNavigate("blog")}
							>
								<button>Blog</button>
							</div>

							<button>EN</button>

							{!user && (
								<div className="flex gap-4 justify-center items-center py-10 font-medium">
									<div
										className="px-5 py-3 border border-gray-500 rounded-xl"
										onClick={() => pageNavigate("auth")}
									>
										<button>Login</button>
									</div>

									<div
										className="px-5 py-3 bg-black text-white rounded-xl"
										onClick={() => pageNavigate("auth")}
									>
										<button>Signup</button>
									</div>
								</div>
							)}
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
};

export default AnimateNav;
